import { Box, Title, Group, Select, ActionIcon, Button, Paper, Text, Flex, Stack, TextInput, Alert, NumberInput, Checkbox } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconGripVertical, IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';
import { SectionProps, FilterItem, FilterOperator, FieldType } from '../../types';
import classes from './Filters.module.css';
import { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DateTimePicker } from '@mantine/dates';
import { getFieldType } from '../../utils';
import { useTranslation } from 'react-i18next';

// Top‐level operator list uses i18n.t() so labels are localizable
type OperatorType = {
  value: FilterOperator,
  label: string;
};

// Sortable filter item component
type SortableFilterItemProps = {
  id: string;
  filter: FilterItem;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onUpdate: ( index: number, filter: FilterItem ) => void;
  getAvailableFields: () => FieldType[];
  isRemoveDisabled: boolean;
  filterOperatorOptions: OperatorType[];
};

function SortableFilterItem({ id, filter, index, isEditing, onEdit, onRemove, onUpdate, getAvailableFields, isRemoveDisabled, filterOperatorOptions }: SortableFilterItemProps ) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [ editingValues, setEditingValues ] = useState<FilterItem>( filter );
  const { t } = useTranslation( 'widgets' );

  const style = {
    transform: CSS.Transform.toString( transform ),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const getOperatorLabel = ( operatorValue: string ) => {
    const operator = filterOperatorOptions.find( op => op.value === operatorValue );
    return operator ? operator.label : operatorValue;
  };

  // Render the appropriate input component based on field type
  const renderValueInput = () => {
    if ( editingValues.operator === 'exists' || editingValues.operator === 'notExists' ) {
      return null;
    }

    const fieldType = getFieldType({ getAvailableFields, field: editingValues.field });

    switch( fieldType ) {
      case 'number':
        return (
          <NumberInput
            label={t( 'widget_builder_filters_value_label','Value' )}
            placeholder={t( 'widget_builder_filters_numeric_value_placeholder','Enter numeric value' )}
            value={editingValues.value === '' ? '' : Number( editingValues.value )}
            onChange={( value ) => setEditingValues({ ...editingValues, value: value?.toString() || '' })}
            className={classes.valueSelect}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            label={t( 'widget_builder_filters_value_label','Value' )}
            checked={editingValues.value === 'true'}
            onChange={( event ) => setEditingValues({ ...editingValues, value: event.currentTarget.checked.toString() })}
            mt={24}
          />
        );
      case 'datetime':
        return (
          <DateTimePicker
            label={t( 'widget_builder_filters_value_label','Value' )}
            placeholder={t( 'widget_builder_filters_datetime_value_placeholder','Select date and time' )}
            value={editingValues.value ? new Date( editingValues.value ) : null}
            onChange={( date ) => setEditingValues({ ...editingValues, value: date ? date.toISOString() : '' })}
            className={classes.valueSelect}
          />
        );
      default:
        return (
          <TextInput
            label={t( 'widget_builder_filters_value_label','Value' )}
            placeholder={t( 'widget_builder_filters_value_placeholder','Enter value' )}
            value={editingValues.value}
            onChange={( event ) => setEditingValues({ ...editingValues, value: event.currentTarget.value })}
            className={classes.valueSelect}
          />
        );
    }
  };

  const handleFinishEdit = () => {
    onUpdate( index, editingValues );
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      p="sm"
      withBorder={false}
      mb="xs"
      radius="xs"
      className={classes.filterPaper}
    >
      {isEditing ? (
        <Group className={classes.editForm}>
          <Select
            label={t( 'widget_builder_filters_field_label','Field' )}
            placeholder={t( 'widget_builder_filters_field_placeholder','Select field' )}
            data={getAvailableFields()}
            value={editingValues.field}
            allowDeselect={false}
            onChange={( value ) => setEditingValues({ ...editingValues, field: value || '' })}
            className={classes.fieldSelect}
            checkIconPosition="right"
          />
          <Select
            label={t( 'widget_builder_filters_operator_label','Operator' )}
            placeholder={t( 'widget_builder_filters_operator_placeholder','Select operator' )}
            data={filterOperatorOptions}
            value={editingValues.operator}
            allowDeselect={false}
            onChange={( value ) => setEditingValues({ ...editingValues, operator: value as FilterItem['operator'] })}
            className={classes.operatorSelect}
            checkIconPosition="right"
          />
          {renderValueInput()}
          <Button onClick={handleFinishEdit} mt={24} disabled={!editingValues.field || !editingValues.operator || ( !editingValues.value && editingValues.operator !== 'exists' && editingValues.operator !== 'notExists' )}>
            {t( 'widget_builder_filters_done_button','Done' )}
          </Button>
        </Group>
      ) : (
        <Flex justify="space-between" align="center" gap="md">
          <div {...attributes} {...listeners} className={classes.dragHandle}>
            <IconGripVertical size={16} stroke={1.5} />
          </div>
          <div className={classes.filterInfo}>
            <Text size="sm">
              {filter.field.split( '.' )[1] || filter.field} {getOperatorLabel( filter.operator )} {filter.operator !== 'exists' && filter.operator !== 'notExists' ? filter.value : ''}
            </Text>
          </div>
          <Group>
            <ActionIcon color="primary.6" variant="subtle" onClick={onEdit}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon color="danger.6" variant="subtle" onClick={onRemove} disabled={isRemoveDisabled}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Flex>
      )}
    </Paper>
  );
}

export default function FiltersSection({ getAvailableFields, updateConfig, config, validationErrors }: SectionProps ) {
  const { t } = useTranslation( 'widgets' );
  const filters = config.filters || [];
  const [ showAddForm, setShowAddForm ] = useState( false );
  const [ editingIndex, setEditingIndex ] = useState<number | null>( null );
  const [ newFilter, setNewFilter ] = useState<FilterItem>({ field: '', operator: 'eq', value: '' });

  const filterOperatorOptions: OperatorType[] = [
  { value: 'eq', label: t( 'widget_builder_filters_operator_eq', 'equals (=)' ) },
  { value: 'neq', label: t( 'widget_builder_filters_operator_neq', 'not equals (≠)' ) },
  { value: 'lt', label: t( 'widget_builder_filters_operator_lt', 'less than (<)' ) },
  { value: 'gt', label: t( 'widget_builder_filters_operator_gt', 'greater than (>)' ) },
  { value: 'lte', label: t( 'widget_builder_filters_operator_lte', 'less or equal (≤)' ) },
  { value: 'gte', label: t( 'widget_builder_filters_operator_gte', 'greater or equal (≥)' ) },
  { value: 'in', label: t( 'widget_builder_filters_operator_in', 'is in (list)' ) },
  { value: 'nin', label: t( 'widget_builder_filters_operator_nin', 'is not in (list)' ) },
  { value: 'exists', label: t( 'widget_builder_filters_operator_exists', 'exists' ) },
  { value: 'notExists', label: t( 'widget_builder_filters_operator_notExists', 'does not exist' ) }
];

  // Extract validation errors for this section
  const sectionErrors = validationErrors?.filters;

  const addFilter = () => {
    const newFilters = [ ...filters, { ...newFilter }];
    updateConfig( 'filters', newFilters );
    setNewFilter({ field: '', operator: 'eq', value: '' });
    setShowAddForm( false );
  };

  const removeFilter = ( index: number ) => {
    const newFilters = filters.filter(( _, i ) => i !== index );
    updateConfig( 'filters', newFilters );
    if ( editingIndex === index ) setEditingIndex( null );
  };

  const updateFilter = ( index: number, filter: FilterItem ) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], ...filter };
    updateConfig( 'filters', newFilters );
    setEditingIndex( null );
  };

  const updateNewFilter = ( field: keyof FilterItem, value: any ) => {
    setNewFilter( prev => ({ ...prev, [field]: value }));
  };
  const getFieldType = () => {
    const selectedField = getAvailableFields().find( field => field.value === newFilter.field );
    return selectedField?.type || 'string';
  };

  const renderValueInput = () => {
    if ( newFilter.operator === 'exists' || newFilter.operator === 'notExists' ) {
      return null;
    }
    const fieldType = getFieldType();
    switch( fieldType ) {
      case 'number':
        return (
          <NumberInput
            label="Value"
            placeholder="Enter numeric value"
            value={newFilter.value === '' ? '' : Number( newFilter.value )}
            onChange={( value ) => updateNewFilter( 'value', value?.toString() || '' )}
            className={classes.valueSelect}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            label="Value"
            checked={newFilter.value === 'true'}
            onChange={( event ) => updateNewFilter( 'value', event.currentTarget.checked.toString())}
            mt={24}
          />
        );
      case 'datetime':
        return (
          <DateTimePicker
            label="Value"
            placeholder="Select date and time"
            value={newFilter.value ? new Date( newFilter.value ) : null}
            onChange={( date ) => updateNewFilter( 'value', date ? date.toISOString() : '' )}
            className={classes.valueSelect}
          />
        );
      default: // 'string':
        return (
          <TextInput
            label="Value"
            placeholder="Enter value"
            value={newFilter.value}
            onChange={( event ) => updateNewFilter( 'value', event.currentTarget.value )}
            className={classes.valueSelect}
          />
        );
    }
  };
  const sensors = useSensors(
    useSensor( PointerSensor ),
    useSensor( KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = ( event: any ) => {
    const { active, over } = event;
    if ( active.id !== over.id ) {
      const oldIndex = filters.findIndex(( _, i ) => `filter-${i}` === active.id );
      const newIndex = filters.findIndex(( _, i ) => `filter-${i}` === over.id );
      updateConfig( 'filters', arrayMove( filters, oldIndex, newIndex ));
    }
  };

  return (
    <div className={classes.container}>
      <Stack gap="md">
        <Title order={4} className={classes.sectionTitle}>
          {t( 'widget_builder_filters_title','Filters' )}
        </Title>
        <Alert icon={<IconInfoCircle size="1.2rem" />} color="blue.5" variant="light" mb="xs">
          <Text size='sm'>
            {t( 'widget_builder_filters_description','Define filters to limit the data shown in your widget. You can filter by any field in your data source.' )}
          </Text>
        </Alert>

        {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
          <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
            <Text size='sm' c="red.9">
              {t( 'widget_builder_filters_errors_message','Please fix the following errors:' )}
              <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                {Object.entries( sectionErrors ).map(([ field, error ]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Text>
          </Alert>
        )}

        <Box className={classes.content}>
        {/* List of added filters */}
        {filters.length > 0 && (
          <div className={classes.filtersListContainer}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={filters.map(( _, index ) => `filter-${index}` )} strategy={verticalListSortingStrategy}>
                {filters.map(( filter, index ) => (
                  <SortableFilterItem
                    key={index}
                    id={`filter-${index}`}
                    filter={filter}
                    index={index}
                    isEditing={editingIndex === index}
                    onEdit={() => setEditingIndex( index )}
                    onRemove={() => removeFilter( index )}
                    onUpdate={updateFilter}
                    getAvailableFields={getAvailableFields}
                    isRemoveDisabled={false}
                    filterOperatorOptions={filterOperatorOptions}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Add filter form */}
        {showAddForm ? (
          <Paper p="md" className={classes.addForm} bg={'gray.0'}>
            <Group className={classes.itemRow}>
              <Select
                label={t( 'widget_builder_filters_field_label','Field' )}
                placeholder={t( 'widget_builder_filters_field_placeholder','Select field' )}
                data={getAvailableFields()}
                value={newFilter.field}
                onChange={( value ) => updateNewFilter( 'field', value )}
                className={classes.fieldSelect}
                checkIconPosition="right"
              />
              <Select
                label={t( 'widget_builder_filters_operator_label','Operator' )}
                placeholder={t( 'widget_builder_filters_operator_placeholder','Select operator' )}
                data={filterOperatorOptions}
                value={newFilter.operator}
                onChange={( value ) => updateNewFilter( 'operator', value as FilterItem['operator'])}
                className={classes.operatorSelect}
                checkIconPosition="right"
              />
              {renderValueInput()}
            </Group>
            <Group justify="flex-end" mt="md">
              <Button variant="outline" bg={'white'} onClick={() => setShowAddForm( false )}>
                {t( 'widget_builder_filters_cancel_button','Cancel' )}
              </Button>
              <Button
                onClick={addFilter}
                disabled={!newFilter.field || !newFilter.operator || ( !newFilter.value && newFilter.operator !== 'exists' && newFilter.operator !== 'notExists' )}
              >
                {t( 'widget_builder_filters_add_button','Add' )}
              </Button>
            </Group>
          </Paper>
        ) : (
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={() => setShowAddForm( true )}
            className={classes.addButton}
            fullWidth
          >
            {t( 'widget_builder_filters_add_filter_button','Add Filter' )}
          </Button>
        )}
      </Box>
      </Stack>
    </div>
  );
}
